import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  AppBar,
  Toolbar,
  Tabs,
  Tab,
  Alert,
  Button,
  Grid,
  TextField,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListItemButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LuggageIcon from '@mui/icons-material/Luggage';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';
import { useCallback, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-material.css';

ModuleRegistry.registerModules([AllCommunityModule]);

const sanitizeCellValue = (value) => {
  if (value === undefined || value === null) return '';
  return value.toString().trim();
};

const normalizeTimeValue = (value) => {
  if (value === undefined || value === null || value === '') return '';

  if (value instanceof Date) {
    const hours = value.getHours();
    const minutes = value.getMinutes();
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    const fraction = value % 1;
    const totalMinutes = Math.round(fraction * 24 * 60);
    const normalizedMinutes = ((totalMinutes % (24 * 60)) + (24 * 60)) % (24 * 60);
    const hours = Math.floor(normalizedMinutes / 60);
    const minutes = normalizedMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  const raw = value.toString().trim();
  if (!raw) return '';

  const ampmMatch = raw.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (ampmMatch) {
    let hours = parseInt(ampmMatch[1], 10);
    const minutes = parseInt(ampmMatch[2], 10);
    const modifier = ampmMatch[3].toUpperCase();

    if (modifier === 'PM' && hours !== 12) hours += 12;
    if (modifier === 'AM' && hours === 12) hours = 0;

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  const hhmmMatch = raw.match(/^(\d{3,4})$/);
  if (hhmmMatch) {
    const numeric = parseInt(hhmmMatch[1], 10);
    const hours = Math.floor(numeric / 100);
    const minutes = numeric % 100;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  const colonMatch = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (colonMatch) {
    const hours = parseInt(colonMatch[1], 10);
    const minutes = parseInt(colonMatch[2], 10);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  return raw;
};

const buildColumnMap = (headers = []) => {
  const normalized = headers.map(header => sanitizeCellValue(header).toLowerCase());
  const findIndex = (candidates, fallback) => {
    for (const candidate of candidates) {
      const idx = normalized.indexOf(candidate);
      if (idx !== -1) return idx;
    }
    return fallback;
  };

  return {
    belt: findIndex(['belt'], 0),
    driver: findIndex(['driver'], 1),
    pullTime: findIndex(['pull time', 'pull'], 2),
    std: findIndex(['std', 'scheduled', 'sched', 'dept'], 3),
    bookings: findIndex(['bookings', 'loads', 'booked'], 4),
    flight: findIndex(['flight'], 5),
    dest: findIndex(['dest', 'destination', 'to'], 6),
    gate: findIndex(['gate'], 7),
    completed: findIndex(['completed', 'complete', 'done', '✓'], -1),
    notes: findIndex(['notes', 'remarks', 'comments'], -1)
  };
};

const extractFlightsFromSheet = (sheetRows, modKey, columnMap) => {
  const flights = [];
  const completedMap = {};
  const notesMap = {};
  const driversMap = {};
  const gatesMap = {};
  const hasPassdownColumns = columnMap.completed !== -1 || columnMap.notes !== -1;

  const getCell = (row, index) => (Array.isArray(row) && index >= 0 && index < row.length ? row[index] : undefined);

  for (let i = 2; i < sheetRows.length; i++) {
    const row = sheetRows[i];
    if (!row) continue;

    const beltValue = sanitizeCellValue(getCell(row, columnMap.belt));
    if (!beltValue) continue;

    const driverValue = sanitizeCellValue(getCell(row, columnMap.driver));
    const pullTimeValue = normalizeTimeValue(getCell(row, columnMap.pullTime));
    const stdValue = normalizeTimeValue(getCell(row, columnMap.std));
    const bookingsValue = sanitizeCellValue(getCell(row, columnMap.bookings));
    const flightValue = sanitizeCellValue(getCell(row, columnMap.flight));
    const destValue = sanitizeCellValue(getCell(row, columnMap.dest)).toUpperCase();
    const gateValue = sanitizeCellValue(getCell(row, columnMap.gate)).toUpperCase();
    const flightId = `${modKey}-${flightValue || `row${i}`}-${stdValue || 'time'}-${i}`;

    flights.push({
      belt: beltValue,
      driver: driverValue,
      pullTime: pullTimeValue,
      std: stdValue,
      bookings: bookingsValue,
      flight: flightValue,
      dest: destValue,
      gate: gateValue,
      id: flightId,
      mod: modKey
    });

    if (hasPassdownColumns) {
      const completedValue = columnMap.completed !== -1 ? sanitizeCellValue(getCell(row, columnMap.completed)) : '';
      const notesValue = columnMap.notes !== -1 ? sanitizeCellValue(getCell(row, columnMap.notes)) : '';

      if (completedValue === '✓') {
        completedMap[flightId] = true;
      }
      if (notesValue) {
        notesMap[flightId] = notesValue;
      }
      if (driverValue) {
        driversMap[flightId] = driverValue;
      }
      if (gateValue) {
        gatesMap[flightId] = gateValue;
      }
    }
  }

  return {
    flights,
    completedMap,
    notesMap,
    driversMap,
    gatesMap,
    hasPassdownColumns
  };
};

const TPDashboard = () => {
  const [activeTab, setActiveTab] = useState(0); // 0 = MOD1, 1 = MOD2, 2 = Reference
  const [referenceSheetTab, setReferenceSheetTab] = useState(0); // Sub-tab for Reference view
  const [dashboardData, setDashboardData] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [pullStatuses, setPullStatuses] = useState({});
  const [driverAssignments, setDriverAssignments] = useState({});
  const [gateAssignments, setGateAssignments] = useState({});
  const [flightNotes, setFlightNotes] = useState({});
  const [dragActive, setDragActive] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [newSessionDialogOpen, setNewSessionDialogOpen] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const gridRef = useRef();

  const persistSessions = useCallback((updater) => {
    setSessions(prevSessions => {
      const nextSessions = typeof updater === 'function' ? updater(prevSessions) : updater;
      try {
        localStorage.setItem('tpDashboardSessions', JSON.stringify(nextSessions));
      } catch (e) {
        // Likely a quota error — sessions embed whole workbooks. Retry with
        // only the newest sessions so the current one is never silently lost.
        try {
          localStorage.setItem('tpDashboardSessions', JSON.stringify(nextSessions.slice(0, 3)));
        } catch (retryError) {
          console.error('Error saving TP dashboard sessions:', retryError);
        }
      }
      return nextSessions;
    });
  }, []);

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Load sessions from localStorage on mount (with legacy migration)
  useEffect(() => {
    const savedSessionsRaw = localStorage.getItem('tpDashboardSessions');
    if (savedSessionsRaw) {
      try {
        const parsedSessions = JSON.parse(savedSessionsRaw);
        if (Array.isArray(parsedSessions)) {
          setSessions(parsedSessions);
        }
      } catch (e) {
        console.error('Error loading saved TP dashboard sessions:', e);
      }
      return;
    }

    // Legacy single-session storage migration
    const legacyDataRaw = localStorage.getItem('tpDashboardData');
    if (!legacyDataRaw) return;
    try {
      const legacyDashboard = JSON.parse(legacyDataRaw);
      const parseJSON = (value, fallback) => {
        if (!value) return fallback;
        try {
          return JSON.parse(value);
        } catch {
          return fallback;
        }
      };

      const legacySession = {
        id: Date.now(),
        dashboardData: legacyDashboard,
        pullStatuses: parseJSON(localStorage.getItem('tpPullStatuses'), {}),
        driverAssignments: parseJSON(localStorage.getItem('tpDriverAssignments'), {}),
        gateAssignments: parseJSON(localStorage.getItem('tpGateAssignments'), {}),
        flightNotes: parseJSON(localStorage.getItem('tpFlightNotes'), {}),
        uploadTime: legacyDashboard?.uploadTime || new Date().toISOString(),
        fileName: legacyDashboard?.fileName || 'Previous Session'
      };

      persistSessions([legacySession]);
    } catch (e) {
      console.error('Error migrating legacy TP dashboard data:', e);
    } finally {
      localStorage.removeItem('tpDashboardData');
      localStorage.removeItem('tpPullStatuses');
      localStorage.removeItem('tpDriverAssignments');
      localStorage.removeItem('tpGateAssignments');
      localStorage.removeItem('tpFlightNotes');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateCurrentSessionField = useCallback((field, value) => {
    if (!currentSessionId) return;
    persistSessions(prev => {
      let updated = false;
      const nextSessions = prev.map(session => {
        if (session.id !== currentSessionId) return session;
        updated = true;
        return { ...session, [field]: value };
      });
      return updated ? nextSessions : prev;
    });
  }, [currentSessionId, persistSessions]);

  useEffect(() => {
    if (!currentSessionId || !dashboardData) return;
    updateCurrentSessionField('dashboardData', dashboardData);
  }, [currentSessionId, dashboardData, updateCurrentSessionField]);

  useEffect(() => {
    if (!currentSessionId) return;
    updateCurrentSessionField('pullStatuses', pullStatuses);
  }, [currentSessionId, pullStatuses, updateCurrentSessionField]);

  useEffect(() => {
    if (!currentSessionId) return;
    updateCurrentSessionField('driverAssignments', driverAssignments);
  }, [currentSessionId, driverAssignments, updateCurrentSessionField]);

  useEffect(() => {
    if (!currentSessionId) return;
    updateCurrentSessionField('gateAssignments', gateAssignments);
  }, [currentSessionId, gateAssignments, updateCurrentSessionField]);

  useEffect(() => {
    if (!currentSessionId) return;
    updateCurrentSessionField('flightNotes', flightNotes);
  }, [currentSessionId, flightNotes, updateCurrentSessionField]);

  useEffect(() => {
    if (!dashboardData?.allSheets) return;

    const rebuildInstructions = [];
    const evaluateMod = (modKey, stateKey) => {
      const flights = dashboardData[stateKey];
      const recordedCount = dashboardData[`${stateKey}Count`] ?? (Array.isArray(flights) ? flights.length : 0);
      if (recordedCount === 0) return;
      if (Array.isArray(flights) && flights.length > 0) return;

      const sheet = dashboardData.allSheets[modKey];
      if (!Array.isArray(sheet) || sheet.length <= 2) return;

      const columnMap = dashboardData.modMeta?.[modKey]?.columnMap || buildColumnMap(sheet[1] || []);
      const parsed = extractFlightsFromSheet(sheet, modKey, columnMap);
      if (parsed.flights.length === 0) return;

      rebuildInstructions.push({
        stateKey,
        flights: parsed.flights,
        modKey,
        columnMap
      });
    };

    evaluateMod('MOD1', 'mod1');
    evaluateMod('MOD2', 'mod2');

    if (rebuildInstructions.length === 0) return;

    setDashboardData(prev => {
      if (!prev) return prev;
      let changed = false;
      const nextMeta = { ...(prev.modMeta || {}) };
      const next = { ...prev, modMeta: nextMeta };

      rebuildInstructions.forEach(({ stateKey, flights, modKey, columnMap }) => {
        if (flights.length > 0) {
          changed = true;
          next[stateKey] = flights;
          next[`${stateKey}Count`] = flights.length;
          next.modMeta[modKey] = { columnMap };
        }
      });

      return changed ? next : prev;
    });
  }, [dashboardData]);

  const sortedSessions = useMemo(() => {
    if (!sessions || sessions.length === 0) return [];
    return [...sessions].sort((a, b) => {
      const aTime = new Date(a.uploadTime || 0).getTime();
      const bTime = new Date(b.uploadTime || 0).getTime();
      return bTime - aTime;
    });
  }, [sessions]);

  // eslint-disable-next-line no-unused-vars
  const currentSession = useMemo(
    () => sessions.find(session => session.id === currentSessionId) || null,
    [sessions, currentSessionId]
  );

  const parseExcelFile = async (file) => {
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });

      // Parse MOD1 and MOD2 sheets
      const mod1Sheet = workbook.Sheets['MOD1'];
      const mod2Sheet = workbook.Sheets['MOD2'];

      if (!mod1Sheet || !mod2Sheet) {
        throw new Error('Excel file must contain MOD1 and MOD2 sheets');
      }

      const mod1Data = XLSX.utils.sheet_to_json(mod1Sheet, { header: 1 });
      const mod2Data = XLSX.utils.sheet_to_json(mod2Sheet, { header: 1 });

      console.log('MOD1 Data:', mod1Data);
      console.log('MOD2 Data:', mod2Data);

      const mod1Columns = buildColumnMap(mod1Data[1] || []);
      const mod2Columns = buildColumnMap(mod2Data[1] || []);

      // Store all sheets for Reference tab
      const allSheets = {};
      workbook.SheetNames.forEach(sheetName => {
        allSheets[sheetName] = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1 });
      });

      const mod1Result = extractFlightsFromSheet(mod1Data, 'MOD1', mod1Columns);
      const mod2Result = extractFlightsFromSheet(mod2Data, 'MOD2', mod2Columns);

      const restoredPullStatuses = {
        ...mod1Result.completedMap,
        ...mod2Result.completedMap
      };
      const restoredDriverAssignments = {
        ...mod1Result.driversMap,
        ...mod2Result.driversMap
      };
      const restoredGateAssignments = {
        ...mod1Result.gatesMap,
        ...mod2Result.gatesMap
      };
      const restoredFlightNotes = {
        ...mod1Result.notesMap,
        ...mod2Result.notesMap
      };

      // Check if this is a PASSDOWN file (has Completed and Notes columns)
      const isPassdownFile = mod1Result.hasPassdownColumns || mod2Result.hasPassdownColumns;
      console.log('Is PASSDOWN file:', isPassdownFile, 'MOD1 headers:', mod1Data[1]);

      const mod1Flights = mod1Result.flights;
      const mod2Flights = mod2Result.flights;

      console.log('Parsed flights - MOD1:', mod1Flights.length, 'MOD2:', mod2Flights.length);

      // Extract date from filename if present (e.g., "Sorted_Gate_Sheet_Wednesday, January 15, 2025.xlsx")
      let scheduleDate = null;
      const dateMatch = file.name.match(/_(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday),?\s+(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},\s+\d{4}/);
      if (dateMatch) {
        scheduleDate = dateMatch[0].substring(1); // Remove the leading underscore
      }

      const dashboard = {
        mod1: mod1Flights,
        mod2: mod2Flights,
        allSheets: allSheets,
        modMeta: {
          MOD1: { columnMap: mod1Columns },
          MOD2: { columnMap: mod2Columns }
        },
        uploadTime: new Date().toISOString(),
        fileName: file.name,
        scheduleDate: scheduleDate,
        mod1Count: mod1Flights.length,
        mod2Count: mod2Flights.length
      };

      console.log(`Loaded MOD1: ${mod1Flights.length} flights, MOD2: ${mod2Flights.length} flights`);
      if (isPassdownFile) {
        console.log('PASSDOWN file detected - restoring state');
      }

      const sessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const newSession = {
        id: sessionId,
        dashboardData: dashboard,
        pullStatuses: restoredPullStatuses,
        driverAssignments: restoredDriverAssignments,
        gateAssignments: restoredGateAssignments,
        flightNotes: restoredFlightNotes,
        uploadTime: dashboard.uploadTime,
        fileName: dashboard.fileName
      };

      // IMPORTANT: Set all state in the correct order to prevent flickering
      // React 18 will batch these automatically, but order matters for derived state
      // Set dashboardData LAST so all other state is ready when it triggers renders
      setActiveTab(0);
      setCurrentSessionId(sessionId);
      setPullStatuses(restoredPullStatuses);
      setDriverAssignments(restoredDriverAssignments);
      setGateAssignments(restoredGateAssignments);
      setFlightNotes(restoredFlightNotes);
      setDashboardData(dashboard); // This must be LAST

      persistSessions(prev => {
        const filtered = prev.filter(session => session.id !== sessionId);
        const nextSessions = [newSession, ...filtered];
        return nextSessions.slice(0, 10);
      });

    } catch (error) {
      console.error('Error parsing Excel:', error);
      alert('Error parsing Excel file. Please ensure you\'re uploading a sorted gate sheet with MOD1 and MOD2 tabs.');
    }
  };

  const handleFile = (file) => {
    if (file && file.name.match(/\.(xlsx|xls)$/)) {
      parseExcelFile(file);
    } else {
      alert('Please upload an Excel file (.xlsx or .xls)');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const getTimeStatus = useCallback((pullTime) => {
    if (!pullTime) return 'normal';

    const now = currentTime;
    const [hours, minutes] = pullTime.split(':').map(Number);
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const pullMinutes = hours * 60 + minutes;

    // Wrap around midnight: interpret the pull time as the occurrence nearest
    // to now, so a 00:05 pull viewed at 23:50 reads as 15 minutes away rather
    // than 23+ hours late.
    const diffMinutes = ((pullMinutes - nowMinutes + 720 + 1440) % 1440) - 720;

    if (diffMinutes < -5) return 'late';
    if (diffMinutes < 0) return 'pulling';
    if (diffMinutes < 15) return 'urgent';
    if (diffMinutes < 30) return 'soon';
    return 'normal';
  }, [currentTime]);

  const getStatusColor = (status) => {
    switch(status) {
      case 'late': return '#d32f2f';
      case 'pulling': return '#ff5722';
      case 'urgent': return '#ff9800';
      case 'soon': return '#ffc107';
      default: return '#4caf50';
    }
  };

  const calculateMetrics = useCallback((modData, statuses, drivers, gates) => {
    if (!modData) return { total: 0, completed: 0, upcoming: 0, late: 0 };

    let completed = 0;
    let upcoming = 0;
    let late = 0;
    const completedFlights = [];
    const upcomingFlights = [];
    const lateFlights = [];

    modData.forEach(flight => {
      const status = getTimeStatus(flight.pullTime);
      const isCompleted = !!statuses[flight.id];
      const gateOverride = gates[flight.id];
      const driverOverride = drivers[flight.id];
      const normalizedGate = gateOverride !== undefined
        ? (typeof gateOverride === 'string' ? gateOverride.trim().toUpperCase() : gateOverride)
        : (flight.gate ? flight.gate.toString().trim().toUpperCase() : '');
      const normalizedDriver = driverOverride !== undefined
        ? (typeof driverOverride === 'string' ? driverOverride.trim() : driverOverride)
        : (flight.driver ? flight.driver.toString().trim() : '');
      const flightWithOverrides = {
        ...flight,
        gate: normalizedGate,
        driver: normalizedDriver
      };

      if (isCompleted) {
        completed++;
        completedFlights.push(flightWithOverrides);
      } else if (status === 'late' || status === 'pulling') {
        late++;
        lateFlights.push(flightWithOverrides);
      } else {
        upcoming++;
        upcomingFlights.push(flightWithOverrides);
      }
    });

    // Sort for display
    completedFlights.sort((a, b) => b.pullTime.localeCompare(a.pullTime)); // Most recent first
    upcomingFlights.sort((a, b) => a.pullTime.localeCompare(b.pullTime)); // Soonest first
    lateFlights.sort((a, b) => a.pullTime.localeCompare(b.pullTime)); // Most urgent first

    return {
      total: modData.length,
      completed,
      upcoming,
      late,
      completionRate: modData.length > 0 ? Math.round((completed / modData.length) * 100) : 0,
      completedFlights: completedFlights.slice(0, 5),
      upcomingFlights: upcomingFlights.slice(0, 5),
      lateFlights: lateFlights.slice(0, 3)
    };
  }, [getTimeStatus]);

  const getCurrentPulls = useCallback((modData, statuses, drivers, gates) => {
    if (!modData) return [];

    return modData.filter(flight => {
      const status = getTimeStatus(flight.pullTime);
      const isCompleted = !!statuses[flight.id];
      const assigned = isCompleted || !!(drivers[flight.id] && drivers[flight.id].trim());
      return ['late', 'pulling', 'urgent'].includes(status) && !assigned;
    }).map(flight => {
      const gateOverride = gates[flight.id];
      const driverOverride = drivers[flight.id];
      const normalizedGate = gateOverride !== undefined
        ? (typeof gateOverride === 'string' ? gateOverride.trim().toUpperCase() : gateOverride)
        : (flight.gate ? flight.gate.toString().trim().toUpperCase() : '');
      const normalizedDriver = driverOverride !== undefined
        ? (typeof driverOverride === 'string' ? driverOverride.trim() : driverOverride)
        : (flight.driver ? flight.driver.toString().trim() : '');

      return {
        ...flight,
        gate: normalizedGate,
        driver: normalizedDriver
      };
    }).slice(0, 5);
  }, [getTimeStatus]);

  const updateDashboardFlight = useCallback((flightId, updater) => {
    setDashboardData(prev => {
      if (!prev) return prev;

      const updateList = (flights = []) => {
        let changed = false;
        const updated = flights.map(flight => {
          if (flight.id === flightId) {
            changed = true;
            return updater(flight);
          }
          return flight;
        });
        return changed ? updated : flights;
      };

      const mod1Flights = prev.mod1 || [];
      const mod2Flights = prev.mod2 || [];
      const updatedMod1 = updateList(mod1Flights);
      const updatedMod2 = updateList(mod2Flights);

      if (updatedMod1 === mod1Flights && updatedMod2 === mod2Flights) {
        return prev;
      }

      return {
        ...prev,
        mod1: updatedMod1,
        mod2: updatedMod2
      };
    });
  }, []);

  const applyDriverUpdate = useCallback((flightId, rawValue) => {
    const value = (rawValue ?? '').toString().trim();
    setDriverAssignments(prev => ({ ...prev, [flightId]: value }));
    updateDashboardFlight(flightId, (flight) => ({ ...flight, driver: value }));
    return value;
  }, [updateDashboardFlight]);

  const applyGateUpdate = useCallback((flightId, rawValue) => {
    const value = (rawValue ?? '').toString().trim().toUpperCase();
    setGateAssignments(prev => ({ ...prev, [flightId]: value }));
    updateDashboardFlight(flightId, (flight) => ({ ...flight, gate: value }));
    return value;
  }, [updateDashboardFlight]);

  const startNewSession = useCallback(() => {
    if (dashboardData) {
      setNewSessionDialogOpen(true);
      return;
    }
    // If no dashboard data, just proceed (this is the initial state)
    setCurrentSessionId(null);
    setDashboardData(null);
    setPullStatuses({});
    setDriverAssignments({});
    setGateAssignments({});
    setFlightNotes({});
  }, [dashboardData]);

  const confirmNewSession = useCallback(() => {
    setCurrentSessionId(null);
    setDashboardData(null);
    setPullStatuses({});
    setDriverAssignments({});
    setGateAssignments({});
    setFlightNotes({});
    setNewSessionDialogOpen(false);
  }, []);

  const cancelNewSession = useCallback(() => {
    setNewSessionDialogOpen(false);
  }, []);

  const handleSessionSelect = useCallback((sessionId) => {
    const session = sessions.find(s => s.id === sessionId);
    if (!session) return;
    setCurrentSessionId(sessionId);
    setDashboardData(session.dashboardData || null);
    setPullStatuses({ ...(session.pullStatuses || {}) });
    setDriverAssignments({ ...(session.driverAssignments || {}) });
    setGateAssignments({ ...(session.gateAssignments || {}) });
    setFlightNotes({ ...(session.flightNotes || {}) });
    setActiveTab(0);
  }, [sessions]);

  const handleDeleteSession = useCallback((sessionId, sessionName) => {
    setSessionToDelete({ id: sessionId, name: sessionName });
    setDeleteDialogOpen(true);
  }, []);

  const confirmDelete = useCallback(() => {
    if (!sessionToDelete) return;

    // If deleting the current session, clear it
    if (sessionToDelete.id === currentSessionId) {
      setCurrentSessionId(null);
      setDashboardData(null);
      setPullStatuses({});
      setDriverAssignments({});
      setGateAssignments({});
      setFlightNotes({});
    }

    // Remove session from list
    persistSessions(prev => prev.filter(s => s.id !== sessionToDelete.id));

    // Close dialog and reset
    setDeleteDialogOpen(false);
    setSessionToDelete(null);
  }, [sessionToDelete, currentSessionId, persistSessions]);

  const cancelDelete = useCallback(() => {
    setDeleteDialogOpen(false);
    setSessionToDelete(null);
  }, []);

  const handleExport = useCallback(() => {
    if (!dashboardData) return;

    // Create a new workbook
    const workbook = XLSX.utils.book_new();

    // Function to create MOD sheet data with Completed and Notes columns
    const createModSheetData = (modFlights, modKey) => {
      // Header rows (row 0: title, row 1: column headers)
      const sheetData = [
        [modKey, '', '', '', '', '', '', '', '', ''], // Title row
        ['Belt', 'Driver', 'Pull Time', 'STD', 'Bookings', 'Flight', 'Dest', 'Gate', 'Completed', 'Notes'] // Headers
      ];

      // Add flight data rows
      modFlights.forEach(flight => {
        const isCompleted = pullStatuses[flight.id] || false;
        const driverValue = driverAssignments[flight.id] !== undefined
          ? driverAssignments[flight.id]
          : flight.driver || '';
        const gateValue = gateAssignments[flight.id] !== undefined
          ? gateAssignments[flight.id]
          : flight.gate || '';
        const notesValue = flightNotes[flight.id] || '';

        sheetData.push([
          flight.belt,
          driverValue,
          flight.pullTime,
          flight.std,
          flight.bookings || '',
          flight.flight,
          flight.dest,
          gateValue,
          isCompleted ? '✓' : '',
          notesValue
        ]);
      });

      return sheetData;
    };

    // Add MOD1 sheet
    const mod1Data = createModSheetData(dashboardData.mod1 || [], 'MOD1');
    const mod1Sheet = XLSX.utils.aoa_to_sheet(mod1Data);
    XLSX.utils.book_append_sheet(workbook, mod1Sheet, 'MOD1');

    // Add MOD2 sheet
    const mod2Data = createModSheetData(dashboardData.mod2 || [], 'MOD2');
    const mod2Sheet = XLSX.utils.aoa_to_sheet(mod2Data);
    XLSX.utils.book_append_sheet(workbook, mod2Sheet, 'MOD2');

    // Add all other reference sheets (excluding MOD1 and MOD2)
    if (dashboardData.allSheets) {
      Object.keys(dashboardData.allSheets).forEach(sheetName => {
        if (sheetName !== 'MOD1' && sheetName !== 'MOD2') {
          const sheetData = dashboardData.allSheets[sheetName];
          const sheet = XLSX.utils.aoa_to_sheet(sheetData);
          XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
        }
      });
    }

    // Generate filename with PASSDOWN appended
    let fileName = dashboardData.fileName || 'Sorted_Gate_Sheet.xlsx';
    // Remove the .xlsx extension
    fileName = fileName.replace(/\.xlsx?$/i, '');
    // Add PASSDOWN and extension
    fileName = `${fileName} PASSDOWN.xlsx`;

    // Write and download the file
    XLSX.writeFile(workbook, fileName);
  }, [dashboardData, pullStatuses, driverAssignments, gateAssignments, flightNotes]);

  // Calculate derived data - memoize to prevent unnecessary recalculations
  const currentMod = useMemo(() => {
    if (!dashboardData) {
      console.log('No dashboardData');
      return [];
    }
    if (activeTab >= 2) return []; // Reference tab
    const modKey = activeTab === 0 ? 'MOD1' : 'MOD2';
    let mod = activeTab === 0 ? dashboardData.mod1 : dashboardData.mod2;

    if (!mod || mod.length === 0) {
      const sheet = dashboardData.allSheets?.[modKey];
      if (Array.isArray(sheet) && sheet.length > 2) {
        const columnMap = dashboardData.modMeta?.[modKey]?.columnMap || buildColumnMap(sheet[1] || []);
        const parsed = extractFlightsFromSheet(sheet, modKey, columnMap);
        if (parsed.flights.length > 0) {
          mod = parsed.flights;
        }
      }
    }

    console.log(`Current MOD${activeTab + 1}:`, mod?.length || 0, 'flights');
    return mod || [];
  }, [dashboardData, activeTab]);

  const metrics = useMemo(() => (
    calculateMetrics(currentMod, pullStatuses, driverAssignments, gateAssignments)
  ), [currentMod, pullStatuses, driverAssignments, gateAssignments, calculateMetrics]);

  const currentPulls = useMemo(() => (
    getCurrentPulls(currentMod, pullStatuses, driverAssignments, gateAssignments)
  ), [currentMod, pullStatuses, driverAssignments, gateAssignments, getCurrentPulls]);

  // Reference sheet names and data (include all sheets including MOD1 and MOD2)
  const referenceSheetNames = useMemo(() => {
    if (!dashboardData?.allSheets) return [];
    return Object.keys(dashboardData.allSheets);
  }, [dashboardData]);

  const currentReferenceSheet = useMemo(() => {
    if (!dashboardData?.allSheets || referenceSheetNames.length === 0) return null;
    const sheetName = referenceSheetNames[referenceSheetTab];
    const sheetData = dashboardData.allSheets[sheetName];

    // MOD1 and MOD2 have a title row at index 0, so we need to skip it
    // Row 0: Title (e.g., "MOD 1 - Belts 1-6")
    // Row 1: Headers (e.g., "Belt", "Driver", etc.)
    // Row 2+: Data
    if (sheetName === 'MOD1' || sheetName === 'MOD2') {
      return sheetData && sheetData.length > 1 ? sheetData.slice(1) : sheetData;
    }

    return sheetData;
  }, [dashboardData, referenceSheetNames, referenceSheetTab]);
  const mod1Total = dashboardData ? (dashboardData.mod1Count ?? (dashboardData.mod1 ? dashboardData.mod1.length : 0)) : 0;
  const mod2Total = dashboardData ? (dashboardData.mod2Count ?? (dashboardData.mod2 ? dashboardData.mod2.length : 0)) : 0;
  const allFlightsTotal = mod1Total + mod2Total;

  // AG Grid setup - define all hooks before any returns
  const CheckboxRenderer = useCallback((props) => {
    if (!props || !props.data) return null;

    const checked = !!props.value;

    return (
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => {
          const nextValue = e.target.checked;

          if (props.setValue) {
            props.setValue(nextValue);
          } else if (props.node && props.node.setDataValue) {
            props.node.setDataValue('completed', nextValue);
          }
          setPullStatuses(prev => ({
            ...prev,
            [props.data.id]: nextValue
          }));
        }}
        style={{
          cursor: 'pointer',
          width: '18px',
          height: '18px',
          accentColor: '#f9b612'
        }}
      />
    );
  }, []);

  const columnDefs = useMemo(() => [
    {
      field: 'completed',
      headerName: '✓',
      width: 60,
      maxWidth: 60,
      cellRenderer: CheckboxRenderer,
      editable: false
    },
    {
      field: 'flight',
      headerName: 'Flight',
      width: 90,
      cellStyle: { fontWeight: 700 }
    },
    {
      field: 'dest',
      headerName: 'Dest',
      width: 70
    },
    {
      field: 'belt',
      headerName: 'Belt',
      width: 100,
      valueFormatter: params => params.value ? `Belt ${params.value}` : ''
    },
    {
      field: 'pullTime',
      headerName: 'Pull Time',
      width: 100
    },
    {
      field: 'std',
      headerName: 'STD',
      width: 80
    },
    {
      field: 'driver',
      headerName: 'Driver',
      width: 120,
      editable: true,
      cellEditor: 'agTextCellEditor',
      valueSetter: (params) => {
        const value = applyDriverUpdate(params.data.id, params.newValue);
        params.data.driver = value;
        return true;
      }
    },
    {
      field: 'gate',
      headerName: 'Gate',
      width: 80,
      editable: true,
      cellEditor: 'agTextCellEditor',
      valueSetter: (params) => {
        const value = applyGateUpdate(params.data.id, params.newValue);
        params.data.gate = value;
        return true;
      }
    },
    {
      field: 'notes',
      headerName: 'Notes',
      flex: 1,
      minWidth: 150,
      editable: true,
      cellEditor: 'agTextCellEditor'
    }
  ], [applyDriverUpdate, applyGateUpdate, CheckboxRenderer]);

  const defaultColDef = useMemo(() => ({
    resizable: true,
    sortable: true,
    filter: false
  }), []);

  const onGridReady = useCallback(() => {
    // Auto sizing handled elsewhere if needed
  }, []);

  const onCellValueChanged = useCallback((event) => {
    const { field, data, newValue } = event;
    const flightId = data.id;

    if (field === 'completed') {
      setPullStatuses(prev => ({ ...prev, [flightId]: newValue }));
    } else if (field === 'notes') {
      const value = (newValue ?? '').toString();
      setFlightNotes(prev => ({ ...prev, [flightId]: value }));
      updateDashboardFlight(flightId, (flight) => ({ ...flight, notes: value }));
    }
  }, [updateDashboardFlight]);

  const getRowClass = useCallback((params) => {
    if (!params.data) return 'tp-row';

    const classes = ['tp-row'];
    const { completed, assigned, status } = params.data;

    if (completed) {
      classes.push('tp-row--completed');
    } else if (assigned) {
      classes.push('tp-row--assigned');
    }

    if (status === 'late' || status === 'pulling') {
      classes.push('tp-row--late');
    } else if (status === 'urgent') {
      classes.push('tp-row--upcoming');
    } else if (status === 'soon') {
      classes.push('tp-row--soon');
    }

    return classes.join(' ');
  }, []);

  // AG Grid row data - must be before any returns
  const rowData = useMemo(() => {
    if (!currentMod || currentMod.length === 0) {
      console.log('No currentMod data');
      return [];
    }
    const data = currentMod.map(flight => {
      const isCompleted = !!pullStatuses[flight.id];
      const driverValueRaw = driverAssignments[flight.id];
      const gateValueRaw = gateAssignments[flight.id];
      const driverValue = typeof driverValueRaw === 'string' ? driverValueRaw.trim() : (driverValueRaw || '');
      const gateValue = gateValueRaw !== undefined
        ? (typeof gateValueRaw === 'string' ? gateValueRaw.trim().toUpperCase() : gateValueRaw)
        : (flight.gate ? flight.gate.toString().trim().toUpperCase() : '');
      const assigned = isCompleted || !!driverValue;

      return {
        ...flight,
        completed: isCompleted,
        assigned,
        driver: driverValue,
        gate: gateValue,
        notes: flightNotes[flight.id] || '',
        status: getTimeStatus(flight.pullTime)
      };
    });

    // Apply search filter
    const filteredData = searchFilter.trim() === '' ? data : data.filter(flight => {
      const searchLower = searchFilter.toLowerCase();
      return (
        (flight.flight && flight.flight.toString().toLowerCase().includes(searchLower)) ||
        (flight.dest && flight.dest.toLowerCase().includes(searchLower)) ||
        (flight.gate && flight.gate.toLowerCase().includes(searchLower)) ||
        (flight.driver && flight.driver.toLowerCase().includes(searchLower)) ||
        (flight.belt && flight.belt.toString().toLowerCase().includes(searchLower))
      );
    });

    console.log('Row data:', filteredData.length, 'rows');
    return filteredData;
  }, [currentMod, pullStatuses, driverAssignments, gateAssignments, flightNotes, searchFilter, getTimeStatus]);

  if (!dashboardData) {
    return (
      <>
        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={cancelDelete}
          PaperProps={{
            sx: {
              backgroundColor: 'rgba(30, 30, 30, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
            }
          }}
        >
          <DialogTitle sx={{ color: '#f9b612', fontWeight: 700 }}>
            Delete Session?
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: '#fff' }}>
              Are you sure you want to delete "{sessionToDelete?.name}"?
            </Typography>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 1 }}>
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={cancelDelete}
              variant="outlined"
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              variant="contained"
              sx={{
                backgroundColor: '#f44336',
                color: '#fff',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#d32f2f'
                }
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* New Session Confirmation Dialog */}
        <Dialog
          open={newSessionDialogOpen}
          onClose={cancelNewSession}
          PaperProps={{
            sx: {
              backgroundColor: 'rgba(30, 30, 30, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
            }
          }}
        >
          <DialogTitle sx={{ color: '#f9b612', fontWeight: 700 }}>
            Start a New Session?
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: '#fff' }}>
              Your current progress is preserved in recent sessions.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={cancelNewSession}
              variant="outlined"
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmNewSession}
              variant="contained"
              sx={{
                backgroundColor: '#f9b612',
                color: '#000',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#e5a510'
                }
              }}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>

        <Box sx={{
          minHeight: '100vh',
          color: '#fff',
          py: 4
        }}>
        <Container maxWidth="md">
          <Paper
            sx={{
              p: 4,
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
            }}
          >
            <Typography variant="h4" gutterBottom sx={{ color: '#f9b612', mb: 3, fontWeight: 700 }}>
              T-Point Dashboard
            </Typography>

            <Alert
              severity="info"
              sx={{
                mb: 3,
                background: 'rgba(33, 150, 243, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(33, 150, 243, 0.3)',
                color: '#fff',
                '& .MuiAlert-icon': {
                  color: '#64b5f6'
                },
                '& .MuiTypography-root': {
                  color: '#fff'
                }
              }}
            >
              <Typography variant="subtitle2" gutterBottom sx={{ color: '#fff' }}>
                <strong>Important Notice:</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                • This dashboard operates entirely locally on your computer<br/>
                • No data is uploaded to any server or database<br/>
                • Data is temporarily stored in your browser's local storage<br/>
                • If you close the browser or clear browser data, you'll need to re-upload the Excel file<br/>
                • Upload the sorted gate sheet Excel file generated from the Piers module
              </Typography>
            </Alert>

            <Box
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              sx={{
                border: '2px dashed',
                borderColor: dragActive ? '#f9b612' : 'rgba(255, 255, 255, 0.3)',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                background: dragActive
                  ? 'linear-gradient(135deg, rgba(249, 182, 18, 0.1) 0%, rgba(255, 193, 7, 0.1) 100%)'
                  : 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: '#f9b612',
                  background: 'rgba(249, 182, 18, 0.05)'
                }
              }}
            >
              <input
                type="file"
                id="file-upload"
                accept=".xlsx,.xls"
                onChange={(e) => handleFile(e.target.files[0])}
                style={{ display: 'none' }}
              />
              <label htmlFor="file-upload" style={{ cursor: 'pointer' }}>
                <CloudUploadIcon sx={{ fontSize: 48, color: '#f9b612', mb: 2 }} />
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Drop your sorted gate sheet here
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  or click to browse
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', mt: 1, display: 'block' }}>
                  Excel file must contain MOD1 and MOD2 sheets
                </Typography>
              </label>
            </Box>

            {sortedSessions.length > 0 && (
              <Box sx={{ mt: 5 }}>
                <Typography variant="h6" sx={{ color: '#f9b612', mb: 2, fontWeight: 600 }}>
                  Recent Sessions
                </Typography>
                <Paper
                  sx={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: 2,
                    overflow: 'hidden'
                  }}
                  elevation={0}
                >
                  <List dense>
                    {sortedSessions.map(session => {
                      const mod1Count = session.dashboardData?.mod1Count ?? session.dashboardData?.mod1?.length ?? 0;
                      const mod2Count = session.dashboardData?.mod2Count ?? session.dashboardData?.mod2?.length ?? 0;
                      return (
                        <ListItem
                          key={session.id}
                          divider
                          sx={{
                            background: 'rgba(0, 0, 0, 0.25)',
                            pr: 12,
                            '&:hover': {
                              background: 'rgba(249, 182, 18, 0.1)'
                            }
                          }}
                        >
                          <ListItemButton onClick={() => handleSessionSelect(session.id)}>
                            <ListItemText
                              primary={session.fileName || 'Session'}
                              secondary={`Uploaded ${new Date(session.uploadTime || Date.now()).toLocaleString()} • MOD1: ${mod1Count} • MOD2: ${mod2Count}`}
                              primaryTypographyProps={{
                                sx: { color: '#fff', fontWeight: 600 }
                              }}
                              secondaryTypographyProps={{
                                sx: { color: 'rgba(255, 255, 255, 0.7)' }
                              }}
                            />
                          </ListItemButton>
                          <ListItemSecondaryAction>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleSessionSelect(session.id);
                                }}
                              >
                                Open
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleDeleteSession(session.id, session.fileName || 'Session');
                                }}
                                sx={{
                                  borderColor: '#f44336',
                                  color: '#f44336',
                                  '&:hover': {
                                    borderColor: '#d32f2f',
                                    backgroundColor: 'rgba(244, 67, 54, 0.1)'
                                  }
                                }}
                              >
                                Delete
                              </Button>
                            </Box>
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                  </List>
                </Paper>
              </Box>
            )}
          </Paper>
        </Container>
      </Box>
      </>
    );
  }

  return (
    <>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDelete}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 30, 30, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
          }
        }}
      >
        <DialogTitle sx={{ color: '#f9b612', fontWeight: 700 }}>
          Delete Session?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#fff' }}>
            Are you sure you want to delete "{sessionToDelete?.name}"?
          </Typography>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={cancelDelete}
            variant="outlined"
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            sx={{
              backgroundColor: '#f44336',
              color: '#fff',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#d32f2f'
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Session Confirmation Dialog */}
      <Dialog
        open={newSessionDialogOpen}
        onClose={cancelNewSession}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 30, 30, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
          }
        }}
      >
        <DialogTitle sx={{ color: '#f9b612', fontWeight: 700 }}>
          Start a New Session?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#fff' }}>
            Your current progress is preserved in recent sessions.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={cancelNewSession}
            variant="outlined"
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmNewSession}
            variant="contained"
            sx={{
              backgroundColor: '#f9b612',
              color: '#000',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#e5a510'
              }
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{
        minHeight: '100vh',
        color: '#fff',
        width: '100vw',
        overflowX: 'hidden'
      }}>
        {/* App Bar */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: 'rgba(30, 30, 30, 0.3)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: 1
        }}
      >
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LuggageIcon sx={{ mr: 2, color: '#f9b612' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              T-Point Dashboard
              {dashboardData?.scheduleDate && (
                <Typography component="span" variant="body1" sx={{ ml: 2, color: '#f9b612', fontWeight: 600 }}>
                  {dashboardData.scheduleDate}
                </Typography>
              )}
            </Typography>
          </Box>
          <Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
            {dashboardData && (
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                (MOD1: {mod1Total} | MOD2: {mod2Total} | Total: {allFlightsTotal})
              </Typography>
            )}
          </Box>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={startNewSession}
            sx={{
              mr: 1,
              color: '#f9b612',
              borderColor: '#f9b612',
              fontWeight: 600,
              '&:hover': {
                borderColor: '#f9b612',
                backgroundColor: 'rgba(249, 182, 18, 0.1)'
              }
            }}
          >
            Upload New File
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={!dashboardData}
            sx={{
              mr: 1,
              backgroundColor: '#f9b612',
              color: '#000',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#e5a510'
              },
              '&:disabled': {
                backgroundColor: 'rgba(249, 182, 18, 0.3)',
                color: 'rgba(0, 0, 0, 0.3)'
              }
            }}
          >
            Export PASSDOWN
          </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* MOD Tabs */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 0,
          backgroundColor: 'rgba(30, 30, 30, 0.3)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          sx={{
            '& .MuiTab-root': {
              color: 'rgba(255, 255, 255, 0.7)',
              fontWeight: 600,
              '&:hover': {
                color: '#f9b612',
                backgroundColor: 'rgba(249, 182, 18, 0.1)'
              }
            },
            '& .Mui-selected': {
              color: '#f9b612',
              fontWeight: 700
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#f9b612',
              height: 3
            }
          }}
        >
          <Tab label="MOD 1" />
          <Tab label="MOD 2" />
          <Tab label="Reference" />
        </Tabs>
      </Paper>

      <Box sx={{
        px: 2,
        py: 2
      }}>
        {/* MOD View (MOD 1 and MOD 2) */}
        {activeTab < 2 && (
          <>
        {/* Completion Progress */}
        <Paper sx={{
          p: 2,
          mb: 2,
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.2)'
        }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#fff' }}>
            Daily Progress
          </Typography>
          <LinearProgress
            variant="determinate"
            value={metrics.completionRate}
            sx={{
              height: 12,
              borderRadius: 6,
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.1)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 6,
                background: metrics.completionRate > 80
                  ? 'linear-gradient(90deg, #4caf50 0%, #81c784 100%)'
                  : 'linear-gradient(90deg, #ff9800 0%, #ffb74d 100%)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
              }
            }}
          />
          <Typography variant="body2" sx={{ mt: 1, color: '#fff', fontWeight: 500 }}>
            {metrics.completionRate}% Complete
          </Typography>
        </Paper>

        <Grid container spacing={2}>
          {/* Current Pulls Alert */}
          <Grid item xs={12} lg={2.75} md={3.5}>
            <Paper sx={{
              p: 2,
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.2)'
            }}>
              <Typography variant="h5" sx={{ color: '#f9b612', fontWeight: 700, textShadow: '0 2px 4px rgba(0,0,0,0.5)', mb: 2 }}>
                <AccessTimeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Current Pulls
              </Typography>
              {currentPulls.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                  No immediate pulls
                </Typography>
              ) : (
                <List dense sx={{ maxHeight: 500, overflow: 'auto' }}>
                  {currentPulls.map(flight => (
                    <ListItem
                      key={flight.id}
                      sx={{
                        background: '#2a2a2a',
                        borderRadius: 2,
                        mb: 1,
                        border: '2px solid',
                        borderColor: getStatusColor(getTimeStatus(flight.pullTime)),
                        boxShadow: `0 4px 12px ${getStatusColor(getTimeStatus(flight.pullTime))}44`,
                        '& .MuiListItemText-primary': {
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: '1.2rem'
                        },
                        '& .MuiListItemText-secondary': {
                          color: 'rgba(255, 255, 255, 0.95)',
                          fontSize: '1rem',
                          fontWeight: 500
                        }
                      }}
                    >
                      <ListItemText
                        primary={`${flight.flight} - ${flight.dest}`}
                        secondary={`Belt ${flight.belt} • Pull: ${flight.pullTime} • STD: ${flight.std}`}
                      />
                    </ListItem>
                  ))}
              </List>
              )}
            </Paper>
          </Grid>

          {/* Flight List */}
          <Grid item xs={12} lg={9.25} md={8.5}>
            <Paper sx={{
              p: 2,
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.2)'
            }}>
              <Typography variant="h5" sx={{ color: '#f9b612', fontWeight: 700, textShadow: '0 2px 4px rgba(0,0,0,0.5)', mb: 2 }}>
                All Flights - {activeTab === 0 ? 'MOD 1' : 'MOD 2'}
              </Typography>

              {/* Search/Filter Bar */}
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search by flight, destination, gate, driver, or belt..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    color: '#fff',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '&:hover fieldset': {
                      borderColor: '#f9b612'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#f9b612'
                    }
                  },
                  '& .MuiInputBase-input': {
                    color: '#fff'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>🔍</Typography>
                    </InputAdornment>
                  )
                }}
              />

              <Box
                sx={{
                  height: 650,
                  width: '100%',
                  minWidth: 0,
                  '& .tp-grid': {
                    '--ag-background-color': 'rgba(0, 0, 0, 0)',
                    '--ag-foreground-color': '#f5f5f5',
                    '--ag-border-color': 'rgba(80, 80, 80, 0.55)',
                    '--ag-secondary-border-color': 'rgba(60, 60, 60, 0.45)',
                    '--ag-row-border-color': 'rgba(255, 255, 255, 0.05)',
                    '--ag-header-background-color': '#0d0d0d',
                    '--ag-header-foreground-color': '#f9b612',
                    '--ag-odd-row-background-color': 'rgba(0, 0, 0, 0)',
                    '--ag-selected-row-background-color': 'rgba(255, 255, 255, 0.06)',
                    '--ag-row-hover-color': 'rgba(255, 255, 255, 0.06)',
                    '--ag-input-focus-border-color': '#f9b612',
                    '--ag-focus-border-color': 'rgba(255, 255, 255, 0.3)',
                    '--ag-wrapper-border-radius': '12px',
                    '--ag-row-height': '58px',
                    '--ag-header-height': '54px',
                    '--ag-font-size': '14px',
                    '--ag-checkbox-checked-color': '#f9b612',
                    '--ag-checkbox-unchecked-color': 'rgba(255, 255, 255, 0.6)',
                    '--ag-header-column-resize-handle-color': 'rgba(249, 182, 18, 0.6)',
                    '--ag-header-column-resize-handle-height': '55%',
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
                  },
                  '& .tp-grid .ag-root-wrapper': {
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 12px 28px rgba(0, 0, 0, 0.45)'
                  },
                  '& .tp-grid .ag-root-wrapper-body': {
                    backgroundColor: 'rgba(0, 0, 0, 0.35)'
                  },
                  '& .tp-grid .ag-header': {
                    backgroundColor: '#111',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
                  },
                  '& .tp-grid .ag-header-cell-label': {
                    fontWeight: 600,
                    color: '#f9b612',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em'
                  },
                  '& .tp-grid .ag-header-cell': {
                    borderRight: '1px solid rgba(200, 200, 200, 0.12)'
                  },
                  '& .tp-grid .ag-header-cell:last-of-type': {
                    borderRight: 'none'
                  },
                  '& .tp-grid .ag-row': {
                    transition: 'box-shadow 0.15s ease, filter 0.15s ease'
                  },
                  '& .tp-grid .ag-row:hover': {
                    filter: 'brightness(1.05)',
                    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.35)'
                  },
                  '& .tp-grid .ag-cell': {
                    border: 'none',
                    padding: '10px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: 'transparent',
                    color: '#f5f5f5',
                    fontWeight: 500,
                    borderRight: '1px solid rgba(200, 200, 200, 0.12)'
                  },
                  '& .tp-grid .ag-cell.ag-cell-focus:not(.ag-cell-inline-editing)': {
                    border: '2px solid rgba(255, 255, 255, 0.3) !important',
                    filter: 'brightness(1.08)'
                  },
                  '& .tp-grid .ag-cell.ag-cell-inline-editing': {
                    border: 'none !important',
                    boxShadow: 'none !important',
                    padding: '0 !important',
                    overflow: 'hidden !important',
                    height: '58px !important'
                  },
                  '& .tp-grid .ag-cell.ag-cell-inline-editing input': {
                    height: '58px !important',
                    maxHeight: '58px !important',
                    padding: '10px 14px !important',
                    border: '2px solid #f9b612 !important',
                    boxSizing: 'border-box !important',
                    margin: '0 !important',
                    lineHeight: 'normal !important'
                  },
                  '& .tp-grid .ag-row .ag-cell:last-of-type': {
                    borderRight: 'none',
                    borderTopRightRadius: '10px',
                    borderBottomRightRadius: '10px'
                  },
                  '& .tp-grid .ag-row .ag-cell:first-of-type': {
                    borderLeft: '4px solid transparent',
                    borderTopLeftRadius: '10px',
                    borderBottomLeftRadius: '10px'
                  },
                  '& .tp-grid .ag-row.tp-row--late .ag-cell': {
                    backgroundColor: 'rgba(211, 47, 47, 0.25)',
                    borderBottom: '1px solid rgba(239, 83, 80, 0.35)'
                  },
                  '& .tp-grid .ag-row.tp-row--late .ag-cell:first-of-type': {
                    borderLeftColor: '#ef5350'
                  },
                  '& .tp-grid .ag-row.tp-row--upcoming .ag-cell': {
                    backgroundColor: 'rgba(255, 152, 0, 0.22)',
                    borderBottom: '1px solid rgba(255, 152, 0, 0.3)'
                  },
                  '& .tp-grid .ag-row.tp-row--upcoming .ag-cell:first-of-type': {
                    borderLeftColor: '#ffb74d'
                  },
                  '& .tp-grid .ag-row.tp-row--soon .ag-cell': {
                    backgroundColor: 'rgba(255, 214, 0, 0.18)',
                    borderBottom: '1px solid rgba(255, 214, 0, 0.25)'
                  },
                  '& .tp-grid .ag-row.tp-row--soon .ag-cell:first-of-type': {
                    borderLeftColor: '#ffe082'
                  },
                  '& .tp-grid .ag-row.tp-row--assigned:not(.tp-row--completed) .ag-cell': {
                    backgroundColor: 'rgba(67, 160, 71, 0.22)',
                    borderBottom: '1px solid rgba(129, 199, 132, 0.35)'
                  },
                  '& .tp-grid .ag-row.tp-row--assigned:not(.tp-row--completed) .ag-cell:first-of-type': {
                    borderLeftColor: '#81c784'
                  },
                  '& .tp-grid .ag-row.tp-row--completed .ag-cell': {
                    backgroundColor: 'rgba(46, 125, 50, 0.32)',
                    borderBottom: '1px solid rgba(56, 142, 60, 0.45)',
                    color: '#e8f5e9'
                  },
                  '& .tp-grid .ag-row.tp-row--completed .ag-cell:first-of-type': {
                    borderLeftColor: '#66bb6a'
                  },
                  '& .tp-grid .ag-row.ag-row-focus .ag-cell': {
                    boxShadow: 'none',
                    filter: 'brightness(1.06)'
                  }
                }}
              >
                {rowData && rowData.length > 0 ? (
                  <AgGridReact
                    key={`${currentSessionId || 'session'}-${activeTab}`}
                    className="ag-theme-material tp-grid"
                    theme="legacy"
                    ref={gridRef}
                    rowData={rowData}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  selectionColumnDef={{
                    checkboxSelection: false,
                    headerCheckboxSelection: false,
                    width: 0,
                    minWidth: 0,
                    maxWidth: 0,
                    resizable: false,
                    suppressMovable: true,
                    suppressSizeToFit: true,
                    hide: true
                  }}
                  onGridReady={onGridReady}
                  onCellValueChanged={onCellValueChanged}
                  getRowClass={getRowClass}
                  animateRows={false}
                  stopEditingWhenCellsLoseFocus={true}
                  domLayout="normal"
                  suppressHorizontalScroll={false}
                  rowHeight={58}
                  getRowId={(params) => params.data?.id}
                  onFirstDataRendered={(params) => {
                    params.api.sizeColumnsToFit();
                  }}
                />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'rgba(255,255,255,0.5)' }}>
                    <Typography>Loading flight data...</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
          </>
        )}

        {/* Reference View */}
        {activeTab === 2 && (
          <Box>
            {referenceSheetNames.length === 0 ? (
              <Paper sx={{
                p: 3,
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                textAlign: 'center'
              }}>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  No reference sheets available
                </Typography>
              </Paper>
            ) : (
              <>
                {/* Reference Sheet Sub-tabs */}
                <Paper
                  elevation={0}
                  sx={{
                    mb: 2,
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: 2
                  }}
                >
                  <Tabs
                    value={referenceSheetTab}
                    onChange={(e, val) => setReferenceSheetTab(val)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      '& .MuiTab-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontWeight: 600,
                        '&:hover': {
                          color: '#f9b612',
                          backgroundColor: 'rgba(249, 182, 18, 0.1)'
                        }
                      },
                      '& .Mui-selected': {
                        color: '#f9b612',
                        fontWeight: 700
                      },
                      '& .MuiTabs-indicator': {
                        backgroundColor: '#f9b612',
                        height: 3
                      }
                    }}
                  >
                    {referenceSheetNames.map((sheetName, index) => (
                      <Tab key={sheetName} label={sheetName} />
                    ))}
                  </Tabs>
                </Paper>

                {/* Reference Sheet Grid */}
                <Paper sx={{
                  p: 2,
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 2,
                  boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.2)'
                }}>
                  <Typography variant="h5" sx={{ color: '#f9b612', fontWeight: 700, mb: 2 }}>
                    {referenceSheetNames[referenceSheetTab]}
                  </Typography>

                  {currentReferenceSheet && currentReferenceSheet.length > 0 ? (
                    <Box
                      sx={{
                        height: 650,
                        width: '100%',
                        minWidth: 0,
                        '& .reference-grid': {
                          '--ag-background-color': 'rgba(0, 0, 0, 0)',
                          '--ag-foreground-color': '#f5f5f5',
                          '--ag-border-color': 'rgba(80, 80, 80, 0.55)',
                          '--ag-secondary-border-color': 'rgba(60, 60, 60, 0.45)',
                          '--ag-row-border-color': 'rgba(255, 255, 255, 0.05)',
                          '--ag-header-background-color': '#0d0d0d',
                          '--ag-header-foreground-color': '#f9b612',
                          '--ag-odd-row-background-color': 'rgba(0, 0, 0, 0)',
                          '--ag-selected-row-background-color': 'rgba(255, 255, 255, 0.06)',
                          '--ag-row-hover-color': 'rgba(255, 255, 255, 0.06)',
                          '--ag-row-height': '36px',
                          '--ag-header-height': '40px',
                          '--ag-font-size': '13px',
                          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
                        },
                        '& .reference-grid .ag-root-wrapper': {
                          borderRadius: '12px',
                          overflow: 'hidden',
                          boxShadow: '0 12px 28px rgba(0, 0, 0, 0.45)'
                        },
                        '& .reference-grid .ag-root-wrapper-body': {
                          backgroundColor: 'rgba(0, 0, 0, 0.35)'
                        },
                        '& .reference-grid .ag-header': {
                          backgroundColor: '#111',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
                        },
                        '& .reference-grid .ag-header-cell-label': {
                          fontWeight: 600,
                          color: '#f9b612',
                          textTransform: 'uppercase',
                          letterSpacing: '0.02em',
                          fontSize: '12px'
                        },
                        '& .reference-grid .ag-header-cell': {
                          padding: '0 8px',
                          borderRight: '1px solid rgba(200, 200, 200, 0.12)'
                        },
                        '& .reference-grid .ag-cell': {
                          border: 'none',
                          padding: '6px 8px',
                          display: 'flex',
                          alignItems: 'center',
                          backgroundColor: 'transparent',
                          color: '#f5f5f5',
                          fontWeight: 400,
                          fontSize: '12px',
                          borderRight: '1px solid rgba(200, 200, 200, 0.12)',
                          lineHeight: '1.4'
                        },
                        '& .reference-grid .ag-row': {
                          transition: 'box-shadow 0.15s ease',
                          backgroundColor: 'rgba(0, 0, 0, 0.2)'
                        },
                        '& .reference-grid .ag-row:hover': {
                          filter: 'brightness(1.05)',
                          boxShadow: '0 6px 16px rgba(0, 0, 0, 0.35)'
                        }
                      }}
                    >
                      <AgGridReact
                        className="ag-theme-material reference-grid"
                        theme="legacy"
                        rowData={currentReferenceSheet.slice(1).map((row, idx) => {
                          const rowObj = { _rowIndex: idx };
                          currentReferenceSheet[0].forEach((header, colIdx) => {
                            rowObj[`col_${colIdx}`] = row[colIdx] || '';
                          });
                          return rowObj;
                        })}
                        columnDefs={currentReferenceSheet[0].map((header, idx) => ({
                          field: `col_${idx}`,
                          headerName: header || `Column ${idx + 1}`,
                          editable: false,
                          resizable: true,
                          sortable: true,
                          filter: true,
                          minWidth: 80,
                          flex: 0
                        }))}
                        defaultColDef={{
                          resizable: true,
                          sortable: true,
                          filter: true
                        }}
                        domLayout="normal"
                        suppressHorizontalScroll={false}
                        rowHeight={36}
                        headerHeight={40}
                        onFirstDataRendered={(params) => {
                          params.api.sizeColumnsToFit();
                        }}
                      />
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      No data available for this sheet
                    </Typography>
                  )}
                </Paper>
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
    </>
  );
};

export default TPDashboard;
